import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import Logo from "@components/Logo/Logo";
import FatsuLogo from "@assets/images/logo.webp";
import LogoDMDb from "@assets/images/Social-Icons/dmdb_logo.webp";
import LogoItoons from "@assets/images/Social-Icons/itoons_logo.webp";
import LogoFeudr from "@assets/images/Social-Icons/logo-feudr.webp";
import LogoTikTok from "@assets/images/logo-tiktok.webp";
import zumedia from "@assets/images/zumedia.png";
import routes from "@routes/routes";
import { AuthContext } from "@context/AuthContext";
import useResizeObserver from "@hooks/useResizeObserver";
import LangButton from "@components/LangButton/LangButton";
import { EmojiContext } from "@src/context/EmojiProduct";
import useOnclickOutside from "react-cool-onclickoutside";

const youtubeLink = "https://m.youtube.com/channel/UC8XJ4BOm3lhrh5CSLyXVYRA";

const dmdbLink = "https://www.dmdb.com/users/fatsu";
const itoonsLink = "https://www.itoons.com";
const feudrLink = "https://www.feudr.com";
const tiktokLink =
  "https://www.tiktok.com/@happenstancemanor?_t=8k7AUUjur6J&_r=1";

const Footer: React.FC = () => {
  const navRef = useRef<HTMLButtonElement>(null);
  const emojiProduct = useContext(EmojiContext);
  const navigate = useNavigate();
  const isMobileView = useResizeObserver();
  const auth = useContext(AuthContext);
  const [openAccordian, setOpenAccordian] = useState<
    "" | "fatsu" | "more" | "watch" | "terms" | "icons"
  >("");
  const [isNavigationDropdownOpen, setIsNavigationDropdownOpen] =
    useState(false);

  const openInNewWindow = useCallback((url: string) => {
    setIsNavigationDropdownOpen(false);
    setOpenAccordian("");
    window.open(url, "_blank", "noopener");
  }, []);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleToggle = useCallback(
    (type: "" | "fatsu" | "more" | "watch" | "terms" | "icons") => {
      if (openAccordian === type) {
        setOpenAccordian("");
        return;
      }
      setOpenAccordian(type);
    },
    [openAccordian]
  );

  const openInSameTab = useCallback(
    (url: string, isFullUrl: boolean) => {
      setIsNavigationDropdownOpen(false);
      setOpenAccordian("");
      if (!isFullUrl) {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
        navigate(url);
      } else {
        window.location.href = url;
      }
    },
    [navigate]
  );

  useOnclickOutside(
    () => {
      setIsNavigationDropdownOpen(false);
      setOpenAccordian("");
    },
    { refs: [dropdownRef], ignoreClass: ["swal2-container", "nav-bar"] }
  );

  useEffect(() => {
    // if (isNavigationDropdownOpen && window.innerWidth > 767) {
    //   document.body.style.overflow = "hidden";
    // }

    if (isNavigationDropdownOpen) {
      document
        .getElementById("nav-bar-puller")
        ?.scrollIntoView({ behavior: "smooth" });
    }

    // return () => {
    //   document.body.style.overflow = "auto";
    // };
  }, [isNavigationDropdownOpen]);

  // this is added to for footer dropdown
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (dropdownRef?.current && !dropdownRef.current.contains(event.target)) {
        if (navRef?.current && navRef.current.contains(event.target)) {
          return;
        }
        setIsNavigationDropdownOpen(false); // or your menu close logic
        setOpenAccordian("");
      }
    };

    document.addEventListener("click", handleOutsideClick, true); // Capture phase

    return () => {
      document.removeEventListener("click", handleOutsideClick, true);
    };
  }, []);

  //this handles the footer navigation on click
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (navRef?.current && navRef.current.contains(event.target)) {
        setIsNavigationDropdownOpen((prev) => !prev);
        setOpenAccordian("");
      }
    };

    document.addEventListener("click", handleOutsideClick, true); // Capture phase

    return () => {
      document.removeEventListener("click", handleOutsideClick, true);
    };
  }, []);

  return (
    <>
      {/* <FooterSkeleton /> */}

      <footer
        id="footer"
        className="relative dark-pink-bg-dots p-12 py-8 max-sm:p-0 flex max-sm:flex-col-reverse items-center gap-4 max-md:gap-0"
      >
          <button
            className="max-md:hidden"
            role="button"
            onClick={() => {
              setIsNavigationDropdownOpen(false);
              setOpenAccordian("");
              if (window.location.pathname === "/") {
                window.scroll({ left: 0, top: 0, behavior: "instant" });
                navigate(0);
              } else navigate(routes.default);
            }}
            aria-label="Fatsu"
          >
            <Logo
              src={FatsuLogo}
              alt="App-Logo"
              className="w-36 object-contain max-sm:w-32 max-sm:mx-auto"
            />
          </button>
          <div className="w-full flex items-center justify-center max-md:justify-start max-md:w-full gap-2 mx-auto max-md:p-4">
            <div className="flex items-center gap-2 max-md:w-full max-sm:grid max-sm:grid-cols-[50px_1fr_50px]">
              <div></div>
              <div className="flex items-center gap-1 max-sm:justify-center">
                <a
                  className="inline-block max-md:w-[70px]"
                  href="https://zuinc.com/"
                  target="_blank"
                >
                  <img src={zumedia} alt="" />
                </a>
                <div>
                  <a
                    className="inline-block text-white text-lg max-md:text-base hover:text-primary"
                    href="https://zuinc.com/"
                    target="_blank"
                  >
                    A zuMedia Company
                  </a>
                  <h6 className="text-white text-lg max-md:text-sm">
                    © {new Date().getFullYear()} fatSu, Inc
                  </h6>
                </div>
              </div>
              <div className="flex items-center max-md:justify-between gap-2">
                <div className="relative h-8 w-8 overflow-hidden">
                  <LangButton
                    showLangButton={isMobileView ? isMobileView : auth}
                    mobileView={isMobileView ? true : undefined}
                    buttonSytles="text-3xl text-white cursor-pointer flex justify-center relative size-full"
                    iconStyles="ri-global-line w-8 h-8 absolute top-0 cursor-pointer"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="relative max-sm:w-full max-sm:bg-blue max-sm:flex max-sm:justify-center max-sm:py-4">
            <button
              className="flex items-center gap-1 text-white text-lg nav-bar"
              ref={navRef}
            >
              <span className="max-sm:hidden">Navigation</span>{" "}
              <svg
                className={`duration-200 max-sm:hidden ${
                  isNavigationDropdownOpen ? "rotate-0" : "rotate-180"
                }`}
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 8V16M12 16L15 13M12 16L9 13"
                  stroke="#FFBFF7"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M2 12C2 16.714 2 19.071 3.464 20.536C4.93 22 7.286 22 12 22C16.714 22 19.071 22 20.535 20.536C22 19.07 22 16.714 22 12C22 7.286 22 4.929 20.535 3.465C19.072 2 16.714 2 12 2C7.286 2 4.929 2 3.464 3.465C2 4.928 2 7.286 2 12Z"
                  stroke="#FFBFF7"
                  strokeWidth="1.5"
                />
              </svg>
              <svg
                className="mx-auto sm:hidden"
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16.0002 23.2504C16.764 23.2504 17.3832 22.6312 17.3832 21.8674C17.3832 21.1036 16.764 20.4844 16.0002 20.4844C15.2364 20.4844 14.6172 21.1036 14.6172 21.8674C14.6172 22.6312 15.2364 23.2504 16.0002 23.2504Z"
                  fill="#FFBFF7"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M25.8 1.25H6.2C4.88734 1.25053 3.6286 1.77222 2.70041 2.70041C1.77222 3.6286 1.25053 4.88734 1.25 6.2V25.8C1.25053 27.1127 1.77222 28.3714 2.70041 29.2996C3.6286 30.2278 4.88734 30.7495 6.2 30.75H25.8C27.1127 30.7495 28.3714 30.2278 29.2996 29.2996C30.2278 28.3714 30.7495 27.1127 30.75 25.8V6.2C30.7495 4.88734 30.2278 3.6286 29.2996 2.70041C28.3714 1.77222 27.1127 1.25053 25.8 1.25ZM19.736 12.8L16.75 9.811V19.082C17.425 19.2639 18.011 19.6848 18.3988 20.2665C18.7866 20.8482 18.9498 21.551 18.8581 22.244C18.7663 22.9371 18.4259 23.5732 17.9002 24.0339C17.3744 24.4947 16.6991 24.7487 16 24.7487C15.3009 24.7487 14.6256 24.4947 14.0999 24.0339C13.5741 23.5732 13.2337 22.9371 13.1419 22.244C13.0502 21.551 13.2134 20.8482 13.6012 20.2665C13.989 19.6848 14.575 19.2639 15.25 19.082V9.811L12.264 12.8L11.2 11.736L16 6.936L20.8 11.736L19.736 12.8Z"
                  fill="#FFBFF7"
                />
              </svg>
            </button>

            <div
              ref={dropdownRef}
              className={`absolute bottom-8 max-sm:bottom-0 right-0 overflow-hidden blue-bg-dots border border-r-primary rounded-md max-sm:rounded-none w-[320px] max-sm:w-full duration-300 z-[999] transition-all ease-in-out origin-bottom ${
                isNavigationDropdownOpen
                  ? "max-h-[500px] max-sm:max-h-[590px] transition-all"
                  : "max-h-0 drop-animation border-0"
              }`}
            >
              <div className="p-3 flex items-center justify-between sm:hidden">
                <div
                  role="button"
                  onClick={() => {
                    setIsNavigationDropdownOpen(false);
                    setOpenAccordian("");
                    if (window.location.pathname === "/") {
                      window.scroll({ left: 0, top: 0, behavior: "instant" });
                      navigate(0);
                    } else {
                      window.scroll({ left: 0, top: 0, behavior: "instant" });
                      setTimeout(() => {
                        navigate(routes.default);
                      });
                    }
                  }}
                  aria-label="Fatsu-logo"
                >
                  <Logo
                    src={FatsuLogo}
                    alt="App-Logo"
                    className="w-32 object-contain"
                  />
                </div>
                <button
                  onClick={() =>
                    setIsNavigationDropdownOpen(!isNavigationDropdownOpen)
                  }
                >
                  <i className="ri-close-line text-primary text-3xl"></i>
                </button>
              </div>
              <div className="block relative w-full text-left text-gray-700 hover:opacity-85">
                <button
                  onClick={() => handleToggle("fatsu")}
                  className="relative flex items-center justify-between w-full p-3 max-md:py-3 font-semibold cursor-pointer text-start"
                  aria-label="Question"
                >
                  <h2 className="text-primary-gradient text-xl">Fatsu</h2>
                  <i
                    className={`ri-arrow-down-s-line text-primary text-3xl accordion-icon ease-in duration-700 ${
                      openAccordian === "fatsu" ? "rotate-180" : ""
                    }`}
                  ></i>
                </button>
                {openAccordian === "fatsu" && (
                  <div className="px-4 text-2xl leading-normal text-white">
                    <button
                      onClick={() => {
                        if (emojiProduct) {
                          openInSameTab(routes.messages, false);
                        }
                      }}
                      className="text-lg w-full tracking-wider my-1 underline-offset-2 inline-block text-primary duration-150 hover:underline hover:decoration-wavy text-start"
                    >
                      Message from Fatsu
                    </button>
                    <button
                      onClick={() => {
                        if (emojiProduct) {
                          openInSameTab("/product/" + emojiProduct.slug, false);
                        }
                      }}
                      className="text-lg w-full tracking-wider my-1 underline-offset-2 inline-block text-primary duration-150 hover:underline hover:decoration-wavy text-start"
                    >
                      Emojis
                    </button>
                    <button
                      onClick={() => {
                        openInSameTab(routes.fatsuOrigin, false);
                      }}
                      className="text-lg w-full tracking-wider my-1 underline-offset-2 inline-block text-primary duration-150 hover:underline hover:decoration-wavy text-start"
                    >
                      FATSU’S ORIGIN
                    </button>
                    <button
                      onClick={() => {
                        openInSameTab(routes.shop, false);
                      }}
                      className="text-lg w-full tracking-wider my-1 underline-offset-2 inline-block text-primary duration-150 hover:underline hover:decoration-wavy text-start"
                    >
                      Fatsu Shop
                    </button>
                    <button
                      onClick={() => {
                        openInSameTab(routes.news, false);
                      }}
                      className="text-lg w-full tracking-wider my-1 underline-offset-2 inline-block text-primary duration-150 hover:underline hover:decoration-wavy text-start"
                    >
                      Fatsu in Lights
                    </button>
                  </div>
                )}
              </div>
              <div className="block w-full text-left text-gray-700 hover:opacity-85">
                <button
                  onClick={() => handleToggle("more")}
                  className="relative flex items-center justify-between w-full p-3 max-md:py-3 font-semibold cursor-pointer text-start"
                  aria-label="Question"
                >
                  <h2 className="text-primary-gradient text-xl">More</h2>
                  <i
                    className={`ri-arrow-down-s-line text-primary text-3xl accordion-icon ease-in duration-700 ${
                      openAccordian === "more" ? "rotate-180" : ""
                    }`}
                  ></i>
                </button>
                {openAccordian === "more" && (
                  <div className="px-4 text-2xl leading-normal text-white">
                    <button
                      onClick={() => {
                        openInSameTab(routes.facts, false);
                      }}
                      className="text-lg w-full tracking-wider my-1 underline-offset-2 inline-block text-primary duration-150 hover:underline hover:decoration-wavy text-start"
                    >
                      Facts, Stats & FAQ
                    </button>
                    <button
                      onClick={() => {
                        openInSameTab(routes.metrics, false);
                      }}
                      className="text-lg w-full tracking-wider my-1 underline-offset-2 inline-block text-primary duration-150 hover:underline hover:decoration-wavy text-start"
                    >
                      Metrics
                    </button>
                    <button
                      onClick={() => {
                        openInSameTab(routes.contact, false);
                      }}
                      className="text-lg w-full tracking-wider my-1 underline-offset-2 inline-block text-primary duration-150 hover:underline hover:decoration-wavy text-start"
                    >
                      Contact Us
                    </button>
                  </div>
                )}
              </div>
              <div
                typeof="button"
                className="block w-full text-left text-gray-700 hover:opacity-85"
              >
                <button
                  onClick={() => handleToggle("watch")}
                  className="relative flex items-center justify-between w-full p-3 max-md:py-3 font-semibold cursor-pointer text-start"
                  aria-label="Question"
                >
                  <h2 className="text-primary-gradient text-xl">
                    Watch & Listen
                  </h2>
                  <i
                    className={`ri-arrow-down-s-line text-primary text-3xl accordion-icon ease-in duration-700 ${
                      openAccordian === "watch" ? "rotate-180" : ""
                    }`}
                  ></i>
                </button>
                {openAccordian === "watch" && (
                  <div className="px-4 text-2xl leading-normal text-white">
                    <button
                      onClick={() => {
                        openInSameTab(routes.allEpisodes, false);
                      }}
                      className="text-lg w-full tracking-wider my-1 underline-offset-2 inline-block text-primary duration-150 hover:underline hover:decoration-wavy text-start"
                    >
                      Watch the episodes
                    </button>
                    <button
                      onClick={() => {
                        openInSameTab(routes.allMusic, false);
                      }}
                      className="text-lg w-full tracking-wider my-1 underline-offset-2 inline-block text-primary duration-150 hover:underline hover:decoration-wavy text-start"
                    >
                      Listen to the music
                    </button>
                    <button
                      onClick={() => {
                        openInNewWindow(youtubeLink);
                      }}
                      className="text-lg w-full tracking-wider my-1 underline-offset-2 inline-block text-primary duration-150 hover:underline hover:decoration-wavy text-start"
                    >
                      Find the youtube channel
                    </button>
                  </div>
                )}
              </div>
              <div className="block w-full text-left text-gray-700 hover:opacity-85">
                <button
                  onClick={() => handleToggle("terms")}
                  className="relative flex items-center justify-between w-full p-3 max-md:py-3 font-semibold cursor-pointer text-start"
                  aria-label="Question"
                >
                  <h2 className="text-primary-gradient text-xl">Legal</h2>
                  <i
                    className={`ri-arrow-down-s-line text-primary text-3xl accordion-icon ease-in duration-700 ${
                      openAccordian === "terms" ? "rotate-180" : ""
                    }`}
                  ></i>
                </button>
                {openAccordian === "terms" && (
                  <div className="px-4 text-2xl leading-normal text-white">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        // setIsNavigationDropdownOpen(false);
                        // setOpenAccordian("");
                        window.open(
                          import.meta.env.VITE_REACT_LOCAL_HOST + routes.terms,
                          "_blank",
                          "noopener"
                        );
                      }}
                      className="text-lg w-full tracking-wider my-1 underline-offset-2 inline-block text-primary duration-150 hover:underline hover:decoration-wavy text-start"
                    >
                      Terms of use
                    </button>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        // setIsNavigationDropdownOpen(false);
                        // setOpenAccordian("");
                        window.open(
                          import.meta.env.VITE_REACT_LOCAL_HOST + routes.policy,
                          "_blank",
                          "noopener"
                        );
                      }}
                      className="text-lg w-full tracking-wider my-1 underline-offset-2 inline-block text-primary duration-150 hover:underline hover:decoration-wavy text-start"
                    >
                      Privacy Policy
                    </button>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        // setIsNavigationDropdownOpen(false);
                        // setOpenAccordian("");
                        window.open(
                          import.meta.env.VITE_REACT_LOCAL_HOST +
                            routes.cookiePolicy,
                          "_blank",
                          "noopener"
                        );
                      }}
                      className="text-lg w-full tracking-wider my-1 underline-offset-2 inline-block text-primary duration-150 hover:underline hover:decoration-wavy text-start"
                    >
                      Cookie Policy
                    </button>
                  </div>
                )}
              </div>
              <div
                typeof="button"
                className="block w-full text-left text-gray-700 hover:opacity-85"
              >
                <button
                  onClick={() => handleToggle("icons")}
                  className="relative flex items-center justify-between w-full p-3 max-md:py-3 font-semibold cursor-pointer text-start"
                  aria-label="Question"
                >
                  <h2 className="text-primary-gradient text-xl">
                    FatSu with us
                  </h2>
                  <i
                    className={`ri-arrow-down-s-line text-primary text-3xl accordion-icon ease-in duration-700 ${
                      openAccordian === "icons" ? "rotate-180" : ""
                    }`}
                  ></i>
                </button>
                {openAccordian === "icons" && (
                  <div className="flex flex-wrap items-center justify-center gap-2 px-4 pb-3 text-2xl leading-normal text-white">
                    <button
                      onClick={() => {
                        openInNewWindow(dmdbLink);
                      }}
                      className="w-16 h-14 duration-150 hover:scale-90"
                      aria-label="DMDB"
                    >
                      <img
                        src={LogoDMDb}
                        alt="social-logo"
                        className="size-full object-contain"
                      />
                    </button>
                    <button
                      onClick={() => {
                        openInNewWindow(itoonsLink);
                      }}
                      className="w-16 h-14 duration-150 hover:scale-90"
                      aria-label="Itoons"
                    >
                      <img
                        src={LogoItoons}
                        alt="social-logo"
                        className="size-full object-contain"
                      />
                    </button>
                    <button
                      onClick={() => {
                        openInNewWindow(feudrLink);
                      }}
                      className="w-16 h-14 duration-150 hover:scale-90"
                      aria-label="Feudr"
                    >
                      <img
                        src={LogoFeudr}
                        alt="social-logo"
                        className="size-full object-contain"
                      />
                    </button>
                    <button
                      onClick={() => {
                        openInNewWindow(tiktokLink);
                      }}
                      className="w-16 h-14 duration-150 hover:scale-90"
                      aria-label="Tiktok"
                    >
                      <img
                        src={LogoTikTok}
                        alt="social-logo"
                        className="size-full object-contain"
                      />
                    </button>
                    <button
                      onClick={() => {
                        openInNewWindow(youtubeLink);
                      }}
                      className="w-16 h-14 flex items-center justify-center duration-150 hover:scale-90"
                      aria-label="Youtube"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        version="1.1"
                        width="55"
                        height="55"
                        x="0"
                        y="0"
                        viewBox="0 0 90.677 90.677"
                      >
                        <path
                          d="M82.287 45.907c-.937-4.071-4.267-7.074-8.275-7.521-9.489-1.06-19.098-1.065-28.66-1.06-9.566-.005-19.173 0-28.665 1.06-4.006.448-7.334 3.451-8.27 7.521-1.334 5.797-1.35 12.125-1.35 18.094s0 12.296 1.334 18.093c.936 4.07 4.264 7.073 8.272 7.521 9.49 1.061 19.097 1.065 28.662 1.061 9.566.005 19.171 0 28.664-1.061 4.006-.448 7.336-3.451 8.272-7.521 1.333-5.797 1.34-12.124 1.34-18.093-.001-5.97.009-12.297-1.324-18.094zM28.9 50.4h-5.54v29.438h-5.146V50.4h-5.439v-4.822H28.9zm13.977 29.439h-4.629v-2.785c-1.839 2.108-3.585 3.136-5.286 3.136-1.491 0-2.517-.604-2.98-1.897-.252-.772-.408-1.994-.408-3.796V54.311h4.625v18.795c0 1.084 0 1.647.042 1.799.111.718.462 1.082 1.082 1.082.928 0 1.898-.715 2.924-2.166v-19.51h4.629zm17.573-7.662c0 2.361-.159 4.062-.468 5.144-.618 1.899-1.855 2.869-3.695 2.869-1.646 0-3.234-.914-4.781-2.824v2.474h-4.625V45.578h4.625v11.189c1.494-1.839 3.08-2.769 4.781-2.769 1.84 0 3.078.969 3.695 2.88.311 1.027.468 2.715.468 5.132zm17.457-4.259h-9.251v4.525c0 2.363.773 3.543 2.363 3.543 1.139 0 1.802-.619 2.066-1.855.043-.251.104-1.279.104-3.134h4.719v.675c0 1.491-.057 2.518-.099 2.98a6.591 6.591 0 0 1-1.08 2.771c-1.281 1.854-3.179 2.768-5.595 2.768-2.42 0-4.262-.871-5.599-2.614-.981-1.278-1.485-3.29-1.485-6.003v-8.941c0-2.729.447-4.725 1.43-6.015 1.336-1.747 3.177-2.617 5.54-2.617 2.321 0 4.161.87 5.457 2.617.969 1.29 1.432 3.286 1.432 6.015v5.285z"
                          fill="#fff"
                          opacity="1"
                          data-original="#fff"
                        ></path>
                        <path
                          d="M70.978 58.163c-1.546 0-2.321 1.181-2.321 3.541v2.362h4.625v-2.362c-.001-2.36-.774-3.541-2.304-3.541zM53.812 58.163c-.762 0-1.534.36-2.307 1.125v15.559c.772.774 1.545 1.14 2.307 1.14 1.334 0 2.012-1.14 2.012-3.445V61.646c0-2.302-.678-3.483-2.012-3.483zM56.396 34.973c1.705 0 3.479-1.036 5.34-3.168v2.814h4.675V8.82h-4.675v19.718c-1.036 1.464-2.018 2.188-2.953 2.188-.626 0-.994-.37-1.096-1.095-.057-.153-.057-.722-.057-1.817V8.82h-4.66v20.4c0 1.822.156 3.055.414 3.836.47 1.307 1.507 1.917 3.012 1.917zM23.851 20.598v14.021h5.184V20.598L35.271 0h-5.242l-3.537 13.595L22.812 0h-5.455c1.093 3.209 2.23 6.434 3.323 9.646 1.663 4.828 2.701 8.468 3.171 10.952zM42.219 34.973c2.342 0 4.162-.881 5.453-2.641.981-1.291 1.451-3.325 1.451-6.067v-9.034c0-2.758-.469-4.774-1.451-6.077-1.291-1.765-3.11-2.646-5.453-2.646-2.33 0-4.149.881-5.443 2.646-.993 1.303-1.463 3.319-1.463 6.077v9.034c0 2.742.47 4.776 1.463 6.067 1.293 1.76 3.113 2.641 5.443 2.641zm-2.231-18.679c0-2.387.724-3.577 2.231-3.577s2.229 1.189 2.229 3.577v10.852c0 2.387-.722 3.581-2.229 3.581s-2.231-1.194-2.231-3.581z"
                          fill="#fff"
                          opacity="1"
                          data-original="#fff"
                        ></path>
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
      </footer>
    </>
  );
};

export default Footer;
